import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCharacter,
  type CharacterId,
  type CustomSound,
} from "@spieleparadies/shared";

import { getSocket } from "../lib/socket";
import { unlockAudioContexts } from "../lib/audioUnlock";
import { useRoomStore } from "../state/useRoomStore";
import { usePlayerStore } from "../state/usePlayerStore";
import { useGameCatalogStore } from "../state/useGameCatalogStore";
import { useCustomSoundsStore } from "../state/useCustomSoundsStore";
import { CharacterPicker } from "../components/CharacterPicker";
import { CharacterAvatar } from "../components/CharacterAvatar";
import { RoomSoundsPanel } from "../components/RoomSoundsPanel";

export function LobbyPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const room = useRoomStore((s) => s.room);
  const setRoom = useRoomStore((s) => s.setRoom);
  const chat = useRoomStore((s) => s.chat);
  const addChat = useRoomStore((s) => s.addChatMessage);
  const playerId = usePlayerStore((s) => s.id);
  const getTitle = useGameCatalogStore((s) => s.getTitle);
  const clearSounds = useCustomSoundsStore((s) => s.clear);

  const [message, setMessage] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  // Store-Updates (room:update, room:closed, room:sound-*) werden persistent
  // in socket.ts verarbeitet — unabhängig vom React-Lifecycle.
  // Hier registrieren wir nur Listener für Navigation, Chat und Toasts.
  useEffect(() => {
    const socket = getSocket();

    const onClosed = (reason: string) => {
      setToast(reason);
      navigate("/");
    };
    const onChat = (msg: { playerId: string; playerName: string; text: string; ts: number }) => {
      addChat(msg);
    };
    const onError = (msg: string) => setToast(msg);
    const onStart = () => {
      if (code) navigate(`/spielen/${code}`);
    };
    const onSoundUpdate = ({ sound }: { sound: CustomSound }) => {
      setToast(`${sound.recordedBy.playerName} hat „${sound.key}" aufgenommen`);
      window.setTimeout(() => setToast(null), 2200);
    };

    socket.on("room:closed", onClosed);
    socket.on("chat:message", onChat);
    socket.on("error", onError);
    socket.on("game:start", onStart);
    socket.on("room:sound-update", onSoundUpdate);

    return () => {
      socket.off("room:closed", onClosed);
      socket.off("chat:message", onChat);
      socket.off("error", onError);
      socket.off("game:start", onStart);
      socket.off("room:sound-update", onSoundUpdate);
    };
  }, [addChat, navigate, code]);

  useEffect(() => {
    if (!room) {
      navigate("/");
    }
  }, [room, navigate]);

  if (!room) {
    return <p style={{ color: "var(--text-muted)" }}>Lade Raum …</p>;
  }

  const me = room.players.find((p) => p.id === playerId);
  const isHost = me?.isHost ?? false;

  const startGame = () => {
    // Audio entsperren direkt im User-Klick – synchron, damit die
    // Gesture-Kette nicht durch `await` zerbricht (iOS Safari ist da
    // strikt). Erst danach den Server informieren.
    unlockAudioContexts();
    getSocket().emit("room:start");
  };
  const leaveRoom = () => {
    getSocket().emit("room:leave");
    setRoom(null);
    clearSounds();
    navigate("/");
  };
  const sendMessage = () => {
    const text = message.trim();
    if (!text) return;
    getSocket().emit("chat:message", text);
    setMessage("");
  };
  const pickCharacter = (id: CharacterId) => {
    getSocket().emit("player:set-character", { character: id });
  };
  const setMode = (mode: "normal" | "training") => {
    getSocket().emit("room:set-mode", { mode });
  };

  return (
    <>
      <RoomSoundsPanel selfId={playerId ?? null} isHost={isHost} />

      <div className="lobby">
        <div className="card">
          <div className="row" style={{ alignItems: "flex-start" }}>
            <div>
              <h2 style={{ margin: 0 }}>Lobby · {getTitle(room.gameId)}</h2>
              <p style={{ color: "var(--text-muted)", margin: "4px 0 0" }}>
                Teile diesen Code mit deinen Freunden:
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <span className="code-pill">{room.code}</span>
              <div>
                <button
                  className="btn btn-ghost"
                  style={{ marginTop: 6 }}
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      `${window.location.origin}/?code=${room.code}`,
                    );
                    setToast("Einladungslink kopiert");
                    setTimeout(() => setToast(null), 2000);
                  }}
                >
                  Link kopieren
                </button>
              </div>
            </div>
          </div>

          <h3 style={{ marginTop: 24 }}>Spieler ({room.players.length}/{room.maxPlayers})</h3>
          <div className="player-list">
            {room.players.map((player) => {
              const char = getCharacter(player.character);
              return (
                <div key={player.id} className="player-row">
                  <span className="player-dot" style={{ background: player.color }} />
                  <span className="player-avatar">
                    <CharacterAvatar character={char} size={28} />
                  </span>
                  <span style={{ fontWeight: 600 }}>{player.name}</span>
                  <span style={{ color: "var(--text-muted)", fontSize: 13 }}>{char.name}</span>
                  {player.isHost && <span className="tag">Host</span>}
                </div>
              );
            })}
          </div>

          <h3 style={{ marginTop: 24 }}>Dein Charakter</h3>
          <CharacterPicker
            selected={me?.character ?? "default"}
            onSelect={pickCharacter}
          />

          <h3 style={{ marginTop: 24 }}>Spielmodus</h3>
          <div className="mode-picker">
            <ModeTile
              active={room.gameMode === "normal"}
              disabled={!isHost}
              onClick={() => setMode("normal")}
              title="Normal"
              description="Team-Leben: 3. Princess retten gibt XP und gilt als geschafft."
              accent="#ec4899"
            />
            <ModeTile
              active={room.gameMode === "training"}
              disabled={!isHost}
              onClick={() => setMode("training")}
              title="Training"
              description="Unendlich Respawns. Keine XP, Level zählt nicht als abgeschlossen – zum Üben."
              accent="#38bdf8"
            />
          </div>

          <div className="row" style={{ marginTop: 24 }}>
            <button className="btn btn-ghost" onClick={leaveRoom}>
              Verlassen
            </button>
            {isHost ? (
              <button className="btn btn-primary" onClick={startGame}>
                {room.gameMode === "training" ? "Training starten" : "Spiel starten"}
              </button>
            ) : (
              <button className="btn" disabled>
                Wartet auf Host …
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Chat</h3>
          <div className="chat">
            <div className="chat-log">
              {chat.length === 0 && (
                <p style={{ color: "var(--text-muted)" }}>Noch keine Nachrichten.</p>
              )}
              {chat.map((msg, i) => (
                <div key={i} className="msg">
                  <span
                    className="name"
                    style={{
                      color:
                        room.players.find((p) => p.id === msg.playerId)?.color ?? "#fff",
                    }}
                  >
                    {msg.playerName}:
                  </span>
                  <span>{msg.text}</span>
                </div>
              ))}
            </div>
            <div className="row">
              <input
                className="input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Nachricht…"
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              />
              <button className="btn btn-primary" onClick={sendMessage} style={{ flex: "0 0 auto" }}>
                Senden
              </button>
            </div>
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

interface ModeTileProps {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  title: string;
  description: string;
  accent: string;
}

function ModeTile({ active, disabled, onClick, title, description, accent }: ModeTileProps) {
  return (
    <button
      type="button"
      className={`mode-tile ${active ? "active" : ""}`}
      onClick={onClick}
      disabled={disabled && !active}
      style={active ? { borderColor: accent, boxShadow: `0 0 0 1px ${accent}55` } : undefined}
    >
      <span className="mode-title" style={{ color: accent }}>{title}</span>
      <span className="mode-desc">{description}</span>
    </button>
  );
}
